import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast, Toaster } from 'sonner';
import { 
  FlaskConical, Plus, Trash2, Play, Pause, BarChart3, 
  Eye, MousePointer, TrendingUp, RefreshCcw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminABTestingPage = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTest, setNewTest] = useState({
    name: '',
    page: 'home',
    element: 'hero_headline',
    variants: [
      { name: 'Control (A)', headline: '', subheadline: '', weight: 50 },
      { name: 'Variant B', headline: '', subheadline: '', weight: 50 }
    ]
  });

  useEffect(() => {
    loadTests();
  }, []);

  const loadTests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/ab-tests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTests(data);
      }
    } catch (e) {
      toast.error('Failed to load A/B tests');
    } finally {
      setLoading(false);
    }
  };

  const createTest = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const testData = {
        ...newTest,
        variants: newTest.variants.map(v => ({
          ...v,
          id: crypto.randomUUID(),
          impressions: 0,
          clicks: 0,
          conversions: 0
        }))
      };

      const res = await fetch(`${API_URL}/api/admin/ab-tests`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(testData)
      });

      if (res.ok) {
        toast.success('A/B test created!');
        setShowCreateModal(false);
        setNewTest({
          name: '',
          page: 'home',
          element: 'hero_headline',
          variants: [
            { name: 'Control (A)', headline: '', subheadline: '', weight: 50 },
            { name: 'Variant B', headline: '', subheadline: '', weight: 50 }
          ]
        });
        loadTests();
      }
    } catch (e) {
      toast.error('Failed to create test');
    }
  };

  const toggleTestStatus = async (testId, currentStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/admin/ab-tests/${testId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });
      toast.success(currentStatus ? 'Test paused' : 'Test activated');
      loadTests();
    } catch (e) {
      toast.error('Failed to update test');
    }
  };

  const deleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this A/B test?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/admin/ab-tests/${testId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test deleted');
      loadTests();
    } catch (e) {
      toast.error('Failed to delete test');
    }
  };

  const calculateConversionRate = (variant) => {
    if (!variant.impressions) return '0.0%';
    return ((variant.clicks / variant.impressions) * 100).toFixed(1) + '%';
  };

  const getWinningVariant = (variants) => {
    if (!variants || variants.length === 0) return null;
    return variants.reduce((best, current) => {
      const bestRate = best.impressions ? (best.clicks / best.impressions) : 0;
      const currentRate = current.impressions ? (current.clicks / current.impressions) : 0;
      return currentRate > bestRate ? current : best;
    }, variants[0]);
  };

  return (
    <div className="p-8 space-y-6">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <FlaskConical className="text-purple-500" />
            A/B Testing
          </h1>
          <p className="text-text-secondary">Test different headlines to optimize conversions</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadTests} variant="outline">
            <RefreshCcw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus size={16} className="mr-2" />
            New A/B Test
          </Button>
        </div>
      </div>

      {/* Tests List */}
      {loading ? (
        <div className="text-center py-12 text-text-secondary">Loading tests...</div>
      ) : tests.length === 0 ? (
        <Card className="border-border-line">
          <CardContent className="py-12 text-center">
            <FlaskConical size={48} className="mx-auto text-text-secondary mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">No A/B Tests Yet</h3>
            <p className="text-text-secondary mb-4">Create your first test to start optimizing your headlines</p>
            <Button onClick={() => setShowCreateModal(true)} className="bg-purple-600 hover:bg-purple-700">
              <Plus size={16} className="mr-2" />
              Create First Test
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {tests.map((test) => {
            const winner = getWinningVariant(test.variants);
            return (
              <Card key={test.id} className="border-border-line">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {test.name}
                        {test.is_active ? (
                          <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Active</span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">Paused</span>
                        )}
                      </CardTitle>
                      <p className="text-sm text-text-secondary">
                        Page: {test.page} | Element: {test.element}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleTestStatus(test.id, test.is_active)}
                      >
                        {test.is_active ? <Pause size={14} className="mr-1" /> : <Play size={14} className="mr-1" />}
                        {test.is_active ? 'Pause' : 'Start'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteTest(test.id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    {test.variants?.map((variant, idx) => {
                      const isWinner = winner?.id === variant.id && test.variants.length > 1;
                      const totalImpressions = test.variants.reduce((sum, v) => sum + (v.impressions || 0), 0);
                      
                      return (
                        <div 
                          key={variant.id} 
                          className={`p-4 rounded-lg border ${isWinner ? 'border-green-300 bg-green-50' : 'border-border-line bg-page-bg'}`}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <span className="font-medium text-text-primary flex items-center gap-2">
                                {variant.name}
                                {isWinner && totalImpressions > 100 && (
                                  <span className="px-2 py-0.5 text-xs bg-green-600 text-white rounded-full flex items-center gap-1">
                                    <TrendingUp size={10} /> Leading
                                  </span>
                                )}
                              </span>
                              <p className="text-sm text-text-secondary">Weight: {variant.weight}%</p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-text-primary">{calculateConversionRate(variant)}</div>
                              <div className="text-xs text-text-secondary">Click Rate</div>
                            </div>
                          </div>
                          
                          <div className="bg-white p-3 rounded border border-border-line mb-3">
                            <p className="font-semibold text-text-primary">{variant.headline || 'No headline set'}</p>
                            <p className="text-sm text-text-secondary">{variant.subheadline || 'No subheadline set'}</p>
                          </div>
                          
                          <div className="flex gap-6 text-sm">
                            <div className="flex items-center gap-1 text-text-secondary">
                              <Eye size={14} />
                              <span>{variant.impressions || 0} impressions</span>
                            </div>
                            <div className="flex items-center gap-1 text-text-secondary">
                              <MousePointer size={14} />
                              <span>{variant.clicks || 0} clicks</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Test Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Create A/B Test</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Test Name</label>
                <Input
                  value={newTest.name}
                  onChange={(e) => setNewTest({ ...newTest, name: e.target.value })}
                  placeholder="e.g., Hero Headline Test Q1 2026"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Page</label>
                  <select
                    value={newTest.page}
                    onChange={(e) => setNewTest({ ...newTest, page: e.target.value })}
                    className="w-full px-3 py-2 border border-border-line rounded-md"
                  >
                    <option value="home">Homepage</option>
                    <option value="features-pricing">Features & Pricing</option>
                    <option value="roi-calculator">ROI Calculator</option>
                    <option value="ai-demo">AI Demo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Element</label>
                  <select
                    value={newTest.element}
                    onChange={(e) => setNewTest({ ...newTest, element: e.target.value })}
                    className="w-full px-3 py-2 border border-border-line rounded-md"
                  >
                    <option value="hero_headline">Hero Headline</option>
                    <option value="cta_button">CTA Button Text</option>
                    <option value="value_prop">Value Proposition</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <label className="block text-sm font-medium text-text-primary">Variants</label>
                {newTest.variants.map((variant, idx) => (
                  <div key={idx} className="p-4 bg-page-bg rounded-lg border border-border-line space-y-3">
                    <div className="flex justify-between items-center">
                      <Input
                        value={variant.name}
                        onChange={(e) => {
                          const variants = [...newTest.variants];
                          variants[idx].name = e.target.value;
                          setNewTest({ ...newTest, variants });
                        }}
                        placeholder="Variant name"
                        className="max-w-[200px]"
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-text-secondary">Weight:</span>
                        <Input
                          type="number"
                          value={variant.weight}
                          onChange={(e) => {
                            const variants = [...newTest.variants];
                            variants[idx].weight = parseInt(e.target.value) || 50;
                            setNewTest({ ...newTest, variants });
                          }}
                          className="w-20"
                          min="1"
                          max="100"
                        />
                        <span className="text-sm text-text-secondary">%</span>
                      </div>
                    </div>
                    <Input
                      value={variant.headline}
                      onChange={(e) => {
                        const variants = [...newTest.variants];
                        variants[idx].headline = e.target.value;
                        setNewTest({ ...newTest, variants });
                      }}
                      placeholder="Headline text"
                    />
                    <Input
                      value={variant.subheadline}
                      onChange={(e) => {
                        const variants = [...newTest.variants];
                        variants[idx].subheadline = e.target.value;
                        setNewTest({ ...newTest, variants });
                      }}
                      placeholder="Subheadline text"
                    />
                  </div>
                ))}
                
                <Button
                  variant="outline"
                  onClick={() => setNewTest({
                    ...newTest,
                    variants: [...newTest.variants, { name: `Variant ${String.fromCharCode(65 + newTest.variants.length)}`, headline: '', subheadline: '', weight: 50 }]
                  })}
                  className="w-full"
                >
                  <Plus size={16} className="mr-2" />
                  Add Variant
                </Button>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button onClick={createTest} className="bg-purple-600 hover:bg-purple-700">Create Test</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminABTestingPage;
